/**
 * Deterministic Step-UUID derived from (card_id, phase, step_num).
 *
 * Single (card × phase × step) → STABLE UUID. Across page reloads and across
 * users looking at the same card, the UUID is the same — so Thomas can write
 * "Schritt b342523m-..." in a message and I can navigate to exactly that step.
 *
 * Not cryptographic — uses a simple double-FNV-1a 64-bit hash, formatted into
 * UUID v5-like shape (variant + version nibbles set). Same input → same output.
 */

function fnv1a64(s: string, seed: number): { hi: number; lo: number } {
  // FNV-1a 64-bit, JS-friendly split into two 32-bit ints.
  // Constants from official FNV reference.
  let hHi = 0xcbf29ce4 ^ seed;
  let hLo = 0x84222325 ^ (seed * 0x9e3779b9);
  for (let i = 0; i < s.length; i++) {
    hLo = (hLo ^ s.charCodeAt(i)) >>> 0;
    // 64-bit FNV prime = 0x100000001b3 ≈ multiply by ~2^40
    const tmpHi = (hHi * 0x1b3 + Math.floor(hLo * 0x100000001 / 0x100000000)) >>> 0;
    const tmpLo = (hLo * 0x1b3) >>> 0;
    hHi = tmpHi;
    hLo = tmpLo;
  }
  return { hi: hHi >>> 0, lo: hLo >>> 0 };
}

function hex32(n: number, width: number): string {
  return n.toString(16).padStart(width, "0").slice(-width);
}

/** Produces an RFC-4122-shaped UUID string (version 5 marker, variant 8).
 * Deterministic — same input always yields the same UUID. */
export function stepUuid(cardId: string, phase: string, stepNum: number | string): string {
  const key = `${cardId}|${phase}|${stepNum}`;
  const a = fnv1a64(key, 1);
  const b = fnv1a64(key, 2);
  const c = fnv1a64(key, 3);
  const d = fnv1a64(key, 4);
  // Compose 32 hex chars, then mask version (5) and variant (8/9/a/b).
  const p1 = hex32(a.hi, 8);
  const p2 = hex32(a.lo, 4);
  const v = 5;
  const p3 = (v.toString(16) + hex32(b.hi & 0x0fff, 3)).slice(-4);
  const variant = ((b.lo >>> 28) & 0x3) | 0x8;
  const p4 = (variant.toString(16) + hex32(b.lo & 0x0fffffff, 3).slice(-3)).slice(-4);
  const p5 = (hex32(c.hi, 8) + hex32(d.lo, 4)).slice(0, 12);
  return `${p1}-${p2}-${p3}-${p4}-${p5}`;
}

/** Short prefix for displaying alongside the full UUID (first 8 chars). */
export function stepUuidShort(uuid: string): string {
  return uuid.slice(0, 8);
}
